import { useEffect, useState } from "react";
import { FileSignature, Plus, Trash2, Download, Pencil, X, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const inputCls = "w-full bg-sand border border-ink/15 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-terra/50";
const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { rider_name: "", rider_phone: "", emergency_contact: "", id_type: "Aadhaar", id_number: "", dl_number: "", rider_address: "", vehicle_name: "", reg_number: "",
  pickup_at: "", return_at: "", deposit: 2000, rental_amount: 0, fuel_level: "Full tank", odometer: "", helmets: "1", damage_notes: "", agreement_date: today(), enquiry_id: "" };

export const agreementFromEnquiry = (e) => {
  const start = e.booking_date ? `${e.booking_date}T09:00` : "";
  const hours = Number(e.duration_hours || 24) * Number(e.days || 1);
  const end = e.booking_date ? new Date(new Date(`${e.booking_date}T09:00`).getTime() + hours * 3600000) : null;
  const pad = (n) => String(n).padStart(2, "0");
  return { ...EMPTY, enquiry_id: e.id, rider_name: e.name || "", rider_phone: e.phone || "", vehicle_name: e.vehicle_name || "", pickup_at: start,
    return_at: end ? `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}T${pad(end.getHours())}:${pad(end.getMinutes())}` : "", rental_amount: Number(e.amount || e.estimate || 0) };
};

const FIELDS = [
  ["rider_name", "Rider full name *"], ["rider_phone", "Phone / WhatsApp"], ["emergency_contact", "Emergency contact (name · phone)"],
  ["id_type", "ID type", "select", ["Aadhaar", "Driving licence", "Passport", "Voter ID"]], ["id_number", "ID number"], ["dl_number", "Driving licence no."],
  ["rider_address", "Address", "wide"],
  ["vehicle_name", "Vehicle"], ["reg_number", "Registration no."], ["agreement_date", "Agreement date", "date"],
  ["pickup_at", "Pickup date & time", "datetime-local"], ["return_at", "Return by", "datetime-local"], ["deposit", "Security deposit ₹", "number"],
  ["rental_amount", "Rental charge ₹", "number"], ["fuel_level", "Fuel level at pickup", "select", ["Full tank", "3/4 tank", "Half tank", "1/4 tank", "Reserve"]], ["odometer", "Odometer (km)"],
  ["helmets", "Helmets issued", "select", ["1", "2"]], ["damage_notes", "Existing damage / remarks", "wide"],
];

function AgreementForm({ draft, onSaved, onCancel }) {
  const [f, setF] = useState(draft);
  const [vehicles, setVehicles] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {}); }, []);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.rider_name.trim()) return toast.error("Rider name is required");
    setBusy(true);
    try {
      const body = { ...f, deposit: Number(f.deposit || 0), rental_amount: Number(f.rental_amount || 0) };
      const r = f.id ? await api.put(`/admin/agreements/${f.id}`, body) : await api.post("/admin/agreements", body);
      toast.success(f.id ? "Agreement updated" : `Agreement ${r.data.number} created`);
      onSaved(r.data);
    } catch (err) { toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="bg-white border border-ink/10 rounded-3xl p-6 space-y-4" data-testid="agreement-form">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-xl font-bold text-ink">{f.id ? `Edit ${f.number}` : "New rental agreement"}</h3>
        <button type="button" onClick={onCancel} className="text-ink/50 hover:text-ink" aria-label="Close" data-testid="agreement-form-cancel"><X size={18} /></button>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        {FIELDS.map(([k, label, type, opts]) => (
          <div key={k} className={type === "wide" ? "sm:col-span-3" : ""}>
            <label className="block text-[11px] font-mono uppercase tracking-widest text-ink/60 mb-1.5">{label}</label>
            {type === "select" ? (
              <select value={f[k] || ""} onChange={(e) => set(k, e.target.value)} className={inputCls} data-testid={`agreement-field-${k}`}>{opts.map((o) => <option key={o}>{o}</option>)}</select>
            ) : k === "vehicle_name" ? (
              <input list="agreement-vehicles" value={f[k] || ""} onChange={(e) => set(k, e.target.value)} className={inputCls} data-testid={`agreement-field-${k}`} />
            ) : (
              <input type={type === "wide" ? "text" : type || "text"} value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} className={inputCls} data-testid={`agreement-field-${k}`} />
            )}
          </div>
        ))}
        <datalist id="agreement-vehicles">{vehicles.map((v) => <option key={v.id} value={v.name} />)}</datalist>
      </div>
      <p className="text-xs text-ink/50">Terms printed on the agreement are edited in Admin → Brand Kit → “Rental agreement terms”.</p>
      <div className="flex justify-end">
        <button type="submit" disabled={busy} className="inline-flex items-center gap-2 bg-ink text-sand text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-terra transition-colors disabled:opacity-60" data-testid="agreement-save-button"><Save size={14} /> {busy ? "Saving…" : f.id ? "Update agreement" : "Create agreement"}</button>
      </div>
    </form>
  );
}

function Row({ a, onEdit, onDeleted }) {
  const id8 = a.id.slice(0, 8);
  const download = async () => {
    try {
      const r = await api.get(`/admin/agreements/${a.id}/pdf`, { responseType: "blob" });
      const el = Object.assign(document.createElement("a"), { href: URL.createObjectURL(r.data), download: `${a.number}.pdf` });
      el.click();
      setTimeout(() => URL.revokeObjectURL(el.href), 5000);
    } catch { toast.error("Could not render PDF"); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete agreement ${a.number}?`)) return;
    await api.delete(`/admin/agreements/${a.id}`);
    onDeleted(a.id);
    toast.success("Agreement deleted");
  };
  return (
    <div className="bg-white border border-ink/10 rounded-2xl p-4 flex flex-wrap items-center gap-3" data-testid={`agreement-row-${id8}`}>
      <div className="flex-1 min-w-[220px]">
        <p className="font-mono text-xs text-terra font-semibold" data-testid={`agreement-number-${id8}`}>{a.number} <span className="text-ink/40 font-normal">· {a.agreement_date}</span></p>
        <p className="font-semibold text-ink mt-0.5">{a.rider_name} {a.rider_phone && <span className="text-ink/40 font-normal">· {a.rider_phone}</span>}</p>
        <p className="text-xs text-ink/60 mt-0.5">{a.vehicle_name}{a.reg_number && ` · ${a.reg_number}`} · {(a.pickup_at || "").replace("T", " ")} → {(a.return_at || "").replace("T", " ")} · deposit ₹{Number(a.deposit || 0).toLocaleString("en-IN")}</p>
      </div>
      <div className="flex gap-1.5">
        <button onClick={download} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-ink text-sand px-3.5 py-2 rounded-full hover:bg-terra transition-colors" data-testid={`agreement-download-${id8}`}><Download size={13} /> Print PDF</button>
        <button onClick={() => onEdit(a)} className="p-2 rounded-full border border-ink/15 hover:bg-ink hover:text-sand transition-colors" aria-label="Edit" data-testid={`agreement-edit-${id8}`}><Pencil size={14} /></button>
        <button onClick={remove} className="p-2 rounded-full border border-ink/15 text-ink/50 hover:text-terra" aria-label="Delete" data-testid={`agreement-delete-${id8}`}><Trash2 size={14} /></button>
      </div>
    </div>
  );
}

export default function AgreementsAdmin({ draft, onDraftConsumed }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  useEffect(() => { api.get("/admin/agreements").then((r) => setRows(r.data)).catch(() => toast.error("Failed to load agreements")); }, []);
  useEffect(() => { if (draft) { setEditing(draft); onDraftConsumed?.(); } }, [draft, onDraftConsumed]);
  const onSaved = (a) => { setRows((arr) => (arr.some((x) => x.id === a.id) ? arr.map((x) => (x.id === a.id ? a : x)) : [a, ...arr])); setEditing(null); };
  return (
    <div className="space-y-6" data-testid="agreements-admin">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl font-bold text-ink flex items-center gap-2"><FileSignature size={20} className="text-terra" /> Rental agreements</h2>
          <p className="text-xs text-ink/60 mt-1">Print, sign at handover, keep one copy with the rider's ID photocopy. Numbered RA-{"{FY}"}-0001.</p>
        </div>
        {!editing && <button onClick={() => setEditing({ ...EMPTY, agreement_date: today() })} className="inline-flex items-center gap-2 bg-ink text-sand text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-terra transition-colors" data-testid="agreement-new-button"><Plus size={14} /> New agreement</button>}
      </div>
      {editing && <AgreementForm draft={editing} onSaved={onSaved} onCancel={() => setEditing(null)} />}
      <div className="space-y-3" data-testid="agreements-list">
        {rows.map((a) => <Row key={a.id} a={a} onEdit={(x) => setEditing(x)} onDeleted={(id) => setRows((arr) => arr.filter((x) => x.id !== id))} />)}
        {rows.length === 0 && !editing && <p className="text-sm text-ink/50 py-8 text-center">No agreements yet — create one from a confirmed rental enquiry or with “New agreement”.</p>}
      </div>
    </div>
  );
}
